/**
 * Case Files Browser — the second face of the Ni² book.
 *
 * Lists every file record associated with the active Case ID as a
 * shelf of folio cards. Combines immutable workbook Imaging records
 * with session-only demo uploads coming from Ni2DemoStore. Demo
 * placeholders are visually badged so viewers can distinguish them
 * from workbook seed data.
 */
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  Search,
  Plus,
  ImageIcon,
  FileText,
  Layers,
  X,
} from "lucide-react";
import { getFilesByCaseId, getCaseById } from "@/data/repository";
import { useNi2DemoStore } from "@/context/Ni2DemoStore";
import { formatCaseDate } from "@/data/viewModels";
import { cn } from "@/lib/utils";

const FILE_TYPE_TONE = {
  Imaging: "blue",
  Screenshot: "amber",
  "Operative photo": "amber",
  Pathology: "green",
  PDF: "red",
};

/* Extension → 3-letter badge shown on the file card */
const extBadge = (name = "") => {
  const bits = String(name).split(".");
  if (bits.length < 2) return "IMG";
  return bits[bits.length - 1].slice(0, 4).toUpperCase();
};

export default function CaseFilesBrowser({
  caseId,
  onBack,
  onSelectFile,
}) {
  const [query, setQuery] = useState("");
  const demoStore = useNi2DemoStore();

  const caseRecord = useMemo(() => getCaseById(caseId), [caseId]);

  const files = useMemo(() => {
    const workbookFiles = getFilesByCaseId(caseId);
    const demoFiles = demoStore.getDemoUploadsForCase(caseId).map((u) => ({
      fileId: u.fileId,
      caseId: u.caseId,
      fileName: u.fileName,
      fileType: u.fileType,
      modality: u.modality,
      studyDate: u.studyDate,
      label: u.label,
      source: "demo",
      demo: true,
    }));
    return [...workbookFiles, ...demoFiles];
  }, [caseId, demoStore]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) =>
      [f.fileName, f.fileType, f.modality, f.sequence, f.label, f.fileId]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [files, query]);

  const handleAddDemoFile = () => {
    const rec = demoStore.createDemoFile({
      caseId,
      fileName: `Screenshot_${new Date().toISOString().slice(0, 10)}.png`,
      fileType: "Screenshot",
      modality: "Photo",
      label: "Local screenshot",
    });
    // Optimistic: no-op (list re-renders from store)
    return rec;
  };

  return (
    <div className="flex h-full flex-col" data-testid="ni2-files-browser">
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between gap-3 px-6 pb-3 pt-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="nn-icon-btn"
            aria-label="Back to Ni² conversation"
            data-testid="ni2-files-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
          </button>
          <div className="leading-tight">
            <div className="nn-eyebrow">Ni² · Files</div>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="text-[15px] font-semibold tracking-tight text-white">
                {caseRecord?.patientLabel || caseId} · files
              </span>
              <span className="font-mono text-[11px] text-[hsl(var(--nn-ink-faint))]">
                {caseId}
              </span>
            </div>
            <div className="mt-0.5 truncate text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
              {caseRecord?.diagnosis} · {files.length} file{files.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="nn-chip nn-chip-blue">Case-linked</span>
          <span className="nn-chip">Provenance on</span>
        </div>
      </header>

      {/* Search bar + Add file */}
      <div className="flex shrink-0 items-center gap-3 px-6 pb-4">
        <div className="flex flex-1 items-center gap-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-3 py-2">
          <Search className="h-3.5 w-3.5 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.8} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search case files, modality, filename…"
            className="w-full bg-transparent text-[12.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] outline-none"
            data-testid="ni2-files-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="rounded-full p-0.5 text-[hsl(var(--nn-ink-faint))] hover:bg-white/[0.06]"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" strokeWidth={1.8} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleAddDemoFile}
          className="nn-btn"
          data-testid="ni2-files-add-demo"
          title="Simulate adding a local screenshot (demo only)"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.8} />
          Add file
        </button>
      </div>

      {/* Shelf */}
      <div className="nn-scroll min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        {filtered.length === 0 ? (
          <div className="mx-auto mt-14 max-w-md rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-center">
            <div className="grid h-10 w-10 mx-auto place-items-center rounded-xl border border-white/[0.08] bg-white/[0.04] text-[hsl(var(--nn-ink-muted))]">
              <Layers className="h-4 w-4" strokeWidth={1.6} />
            </div>
            <div className="mt-3 text-[13px] font-medium text-[hsl(var(--nn-ink))]">
              No files match "{query}"
            </div>
            <div className="mt-1 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
              Every file must be linked to a case; use "Add file" to simulate a local screenshot for
              this demo run.
            </div>
          </div>
        ) : (
          <ShelfGrid files={filtered} onSelectFile={onSelectFile} onAdd={handleAddDemoFile} />
        )}
      </div>
    </div>
  );
}

function ShelfGrid({ files, onSelectFile, onAdd }) {
  return (
    <div className="nn-file-shelf grid grid-cols-6 gap-4 pb-6">
      {files.map((f) => (
        <FileCard key={f.fileId} f={f} onClick={() => onSelectFile?.(f)} />
      ))}
      <AddCard onClick={onAdd} />
    </div>
  );
}

function FileCard({ f, onClick }) {
  const tone = FILE_TYPE_TONE[f.fileType] || "red";
  const isImaging = f.fileType === "Imaging" || f.source === "imaging";
  const Icon = isImaging ? ImageIcon : FileText;

  return (
    <button
      type="button"
      onClick={onClick}
      className="nn-file-card text-left"
      data-tone={tone}
      data-testid={`ni2-file-card-${f.fileId}`}
      aria-label={`Open ${f.fileName}`}
    >
      <div className="mt-3 flex items-center justify-between text-[9.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
        <span>{extBadge(f.fileName)}</span>
        {f.demo && <span className="nn-chip" style={{ padding: "1px 5px", fontSize: 8.5 }}>DEMO</span>}
      </div>
      <div className="mt-1.5 grid h-[46px] w-full place-items-center rounded-md border border-white/[0.06] bg-black/25 text-[hsl(var(--nn-ink-faint))]">
        <Icon className="h-5 w-5" strokeWidth={1.4} />
      </div>
      <div className="mt-2 line-clamp-2 text-[10.5px] font-medium leading-[13px] text-[hsl(var(--nn-ink))]">
        {f.fileName}
      </div>
      <div className="mt-0.5 line-clamp-1 text-[9.5px] text-[hsl(var(--nn-ink-muted))]">
        {f.label || f.fileType}
      </div>
    </button>
  );
}

function AddCard({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "nn-file-card grid place-items-center text-[hsl(var(--nn-ink-muted))]",
        "!bg-white/[0.02]"
      )}
      style={{ borderStyle: "dashed" }}
      data-testid="ni2-files-add-card"
      aria-label="Add file (demo)"
    >
      <div className="grid h-9 w-9 place-items-center rounded-full border border-white/[0.10] bg-white/[0.04]">
        <Plus className="h-4 w-4" strokeWidth={1.8} />
      </div>
      <div className="mt-2 text-[10.5px] font-medium">Add file</div>
      <div className="text-[9.5px] text-[hsl(var(--nn-ink-faint))]">demo</div>
    </button>
  );
}
